import { useState, useEffect, useMemo } from 'react';

import styled from 'styled-components';
import moment from 'moment';
import Lottie from 'lottie-react';

import { useTheme } from '../../../Context/ThemeContext';
import { lightTheme, darkTheme } from '../../../Styles/theme';
import { useLanguage } from '../../../Context/LanguageContext';

import AgeGroupIcon from '../../../image/AgeGroupIcon.svg';
import LoadingAnimation from '../../../Animation/NewLoading.json';

// 연령대 index(0=10세미만 ~ 8=80대) 기준 고정 프로그레스 바 색상 //
// 다크: brand500→900 (점점 어둡게), 라이트: brand500→100 (점점 밝게, Figma) //
const PROGRESS_BAR_COLOR_KEYS_DARK = ['brand500', 'brand550', 'brand600', 'brand650', 'brand700', 'brand750', 'brand800', 'brand850', 'brand900'];
const PROGRESS_BAR_COLOR_KEYS_LIGHT = ['brand500', 'brand450', 'brand400', 'brand350', 'brand300', 'brand250', 'brand200', 'brand150', 'brand100'];

export default function MemberEngagementBox(props) {
  const {
    calcDateText,
    startDate,
    endDate,
    engagementReserves,
    allMembers,
    isOpenPartnerGymSales,
    isEngagementLoading,
    setIsEngagementLoading,
  } = props;

  const { isDark } = useTheme();
  const theme = isDark ? darkTheme : lightTheme;
  const { t } = useLanguage();

  const progressBarColorKeys = isDark ? PROGRESS_BAR_COLOR_KEYS_DARK : PROGRESS_BAR_COLOR_KEYS_LIGHT;

  const [allAgeGroups, setAllAgeGroups] = useState([]);
  const [engagementStates, setEngagementStates] = useState([]);
  const [engagementTotalCount, setEngagementTotalCount] = useState(0);
  const [engagementMaxCount, setEngagementMaxCount] = useState(0);
  const [engagementMaxText, setEngagementMaxText] = useState('');

  // 회원별 출생연도 + 산정기간 내 유효 회원권 사전 파싱 (유효회원만 포함) //
  const parsedAllMembers = useMemo(() => {
    if (!allMembers || !startDate || !endDate) return [];

    return allMembers
      .filter(m => m.birthDay && Array.isArray(m.usages))
      .map(m => ({
        memberId: m.memberId,
        birthYear: parseInt(m.birthDay.slice(0, 4)),
        // 산정기간과 overlap 되는 회원권만 등록 (memShipId/memShipName 누락 방어 포함)
        memberships: m.usages
          .filter(u =>
            u.memberShipId &&
            u.memberShipName &&
            moment(u.startDate).isSameOrBefore(endDate, 'day') &&
            moment(u.endDate).isSameOrAfter(startDate, 'day')
          )
          .map(u => ({
            membershipId: u.memberShipId,
            membershipName: u.memberShipName,
          })),
      }))
      // 유효 회원권이 1개 이상인 회원만 유지 (= 유효회원)
      .filter(m => m.memberships.length > 0);
  }, [allMembers, startDate, endDate]);

  // 연령별 이용자 분석 데이터 가공 //
  useEffect(() => {
    const year = parseInt(moment().format('YYYY'));

    const engagementStateList = [
      { text: t('statistics.engagement.ageUnder10'), startYear: year - 8, endYear: year, totalCount: 0, reservedSum: 0, userIds: new Set(), usagesMap: new Map() },
      { text: t('statistics.engagement.age10s'), startYear: year - 18, endYear: year - 9, totalCount: 0, reservedSum: 0, userIds: new Set(), usagesMap: new Map() },
      { text: t('statistics.engagement.age20s'), startYear: year - 28, endYear: year - 19, totalCount: 0, reservedSum: 0, userIds: new Set(), usagesMap: new Map() },
      { text: t('statistics.engagement.age30s'), startYear: year - 38, endYear: year - 29, totalCount: 0, reservedSum: 0, userIds: new Set(), usagesMap: new Map() },
      { text: t('statistics.engagement.age40s'), startYear: year - 48, endYear: year - 39, totalCount: 0, reservedSum: 0, userIds: new Set(), usagesMap: new Map() },
      { text: t('statistics.engagement.age50s'), startYear: year - 58, endYear: year - 49, totalCount: 0, reservedSum: 0, userIds: new Set(), usagesMap: new Map() },
      { text: t('statistics.engagement.age60s'), startYear: year - 68, endYear: year - 59, totalCount: 0, reservedSum: 0, userIds: new Set(), usagesMap: new Map() },
      { text: t('statistics.engagement.age70s'), startYear: year - 78, endYear: year - 69, totalCount: 0, reservedSum: 0, userIds: new Set(), usagesMap: new Map() },
      { text: t('statistics.engagement.age80s'), startYear: year - 88, endYear: year - 79, totalCount: 0, reservedSum: 0, userIds: new Set(), usagesMap: new Map() },
      { text: t('statistics.engagement.age90s'), startYear: year - 98, endYear: year - 89, totalCount: 0, reservedSum: 0, userIds: new Set(), usagesMap: new Map() },
    ];

    // 출생연도 → 연령대 인덱스 (구간을 벗어나면 -1) //
    function getAgeGroupIndex(birthYear) {
      const age = year - birthYear;

      if (age < 0 || age > 98) return -1;

      return Math.floor((age + 1) / 10);
    }

    // Phase 0: 보유 회원권 사전 등록(count: 0) + 보유 고유 회원 집계 //
    for (const member of parsedAllMembers) {
      const idx = getAgeGroupIndex(member.birthYear);

      if (idx === -1) continue;

      const group = engagementStateList[idx];
      group.userIds.add(member.memberId);

      for (const m of member.memberships) {
        if (!group.usagesMap.has(m.membershipId)) {
          group.usagesMap.set(m.membershipId, {
            membershipId: m.membershipId,
            membershipName: m.membershipName,
            count: 0
          });
        }
      }
    }

    // Phase 1: 사용 실적(reserve) 누적 //
    for (const reserve of engagementReserves) {
      const idx = getAgeGroupIndex(parseInt(reserve.birthDay.slice(0, 4)));

      if (idx === -1) continue;

      const group = engagementStateList[idx];
      group.reservedSum += 1;

      const existing = group.usagesMap.get(reserve.membershipId);

      if (existing) {
        existing.count += 1;
      }
      else {
        group.usagesMap.set(reserve.membershipId, {
          membershipId: reserve.membershipId,
          membershipName: reserve.membershipName,
          count: 1
        });
      }
    }

    let grandTotal = 0;
    let maxText = t('statistics.engagement.ageUnder10');
    let maxCount = 0;

    // Map → 정렬된 배열 변환 + 집계 //
    for (const group of engagementStateList) {
      group.usages = Array.from(group.usagesMap.values())
        .sort((a, b) => b.count - a.count || a.membershipId - b.membershipId);
      group.totalCount = group.userIds.size;
      grandTotal += group.totalCount;

      if (group.totalCount > maxCount) {
        maxText = group.text;
        maxCount = group.totalCount;
      }
    }

    setAllAgeGroups(engagementStateList.slice(0, 9));

    const finalEngagementStates = engagementStateList.filter(group => group.usages.length > 0);

    setEngagementStates(finalEngagementStates);
    setEngagementTotalCount(grandTotal);
    setEngagementMaxCount(maxCount);
    setEngagementMaxText(maxText);
    setIsEngagementLoading(false);
  }, [engagementReserves, parsedAllMembers]);

  // 연령별 비율 계산 //
  function calcPercentage(count) {
    if (engagementTotalCount === 0) return '0';

    return (count / engagementTotalCount * 100).toFixed(1);
  }

  // 프로그레스 바 너비 비율 계산 //
  function calcBarWidth(count) {
    if (engagementMaxCount === 0) return 0;

    return Math.min((count / engagementMaxCount) * 100, 100);
  }

  // 선호 비중 계산 //
  function calcPreference(usageCount, groupTotalCount) {
    if (groupTotalCount === 0) return '0';

    return (usageCount / groupTotalCount * 100).toFixed(0);
  }

  // 숫자 콤마 포맷팅 //
  function formatNumber(num) {
    return num.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  }

  // 프로그레스 바 카드 렌더링 //
  function renderProgressBarCard(data, index) {
    const percentage = calcPercentage(data.totalCount);
    const barWidth = calcBarWidth(data.totalCount);
    const barColor = theme[progressBarColorKeys[index]] || theme.brand500;

    return (
      <ProgressBarCardWrapper key={'ProgressBar-' + index}>
        <ProgressBarInfoRow>
          <ProgressBarLabelWrapper>
            <Text fontFamily='NotoSansKR-Medium' fontSize={14} color={theme.text}>{data.text}</Text>
            <PercentageWrapper>
              <Text fontFamily='NotoSansKR-Medium' fontSize={20} color={theme.brand500}>{percentage}</Text>
              <PercentageSuffix>
                <Text fontFamily='NotoSansKR-Medium' fontSize={12} color={theme.brand500}>%</Text>
              </PercentageSuffix>
            </PercentageWrapper>
          </ProgressBarLabelWrapper>
          <CountWrapper>
            <Text fontFamily='NotoSansKR-Medium' fontSize={20} color={theme.text}>{formatNumber(data.totalCount)}</Text>
            <CountSuffix>
              <Text fontFamily='NotoSansKR-Medium' fontSize={12} color={theme.subText}>{t('statistics.engagement.memberUnit')}</Text>
            </CountSuffix>
          </CountWrapper>
        </ProgressBarInfoRow>
        <ProgressBarTrack>
          {
            data.totalCount > 0 &&
            <ProgressBarFill width={barWidth} $barColor={barColor} />
          }
        </ProgressBarTrack>
      </ProgressBarCardWrapper>
    );
  }

  // 테이블 연령 그룹 행 렌더링 //
  function renderAgeGroupRow(data, index) {
    const percentage = calcPercentage(data.totalCount);
    const isLast = index === engagementStates.length - 1;

    return (
      <AgeGroupRow key={'AgeGroup-' + index}>
        <AgeGroupLabelCell $isLast={isLast}>
          <AgePercentCell>
            <PercentageWrapper>
              <Text fontFamily='NotoSansKR-Bold' fontSize={20} color={theme.brand500}>{percentage}</Text>
              <PercentageSuffix>
                <Text fontFamily='NotoSansKR-Medium' fontSize={12} color={theme.brand500}>%</Text>
              </PercentageSuffix>
            </PercentageWrapper>
          </AgePercentCell>
          <AgeNameCell>
            <Text fontFamily='NotoSansKR-Bold' fontSize={14} color={theme.text}>{data.text}</Text>
          </AgeNameCell>
          <AgeCountCell>
            <Text fontFamily='NotoSansKR-Bold' fontSize={14} color={theme.text}>{formatNumber(data.totalCount)}</Text>
            <CountSuffix>
              <Text fontFamily='NotoSansKR-Medium' fontSize={12} color={theme.text}>{t('statistics.engagement.memberUnit')}</Text>
            </CountSuffix>
          </AgeCountCell>
        </AgeGroupLabelCell>
        <MembershipColumnWrapper>
          {
            data.usages.map((usage, usageIndex) => (
              <TableDataCell
                key={'Membership-' + usageIndex}
                $isLast={isLast && usageIndex === data.usages.length - 1}
                $isGroupLast={usageIndex === data.usages.length - 1}>
                <Text fontFamily='NotoSansKR-Regular' fontSize={14} color={theme.text}>{usage.membershipName}</Text>
              </TableDataCell>
            ))
          }
        </MembershipColumnWrapper>
        <UsageColumnWrapper>
          {
            data.usages.map((usage, usageIndex) => (
              <TableDataCell
                key={'Usage-' + usageIndex}
                $isLast={isLast && usageIndex === data.usages.length - 1}
                $isGroupLast={usageIndex === data.usages.length - 1}
                $center>
                <Text fontFamily='NotoSansKR-Regular' fontSize={14} color={theme.text}>
                  {formatNumber(usage.count) + ' ' + t('statistics.countUnit')}
                </Text>
              </TableDataCell>
            ))
          }
        </UsageColumnWrapper>
        <PreferenceColumnWrapper>
          {
            data.usages.map((usage, usageIndex) => (
              <TableDataCell
                key={'Preference-' + usageIndex}
                $isLast={isLast && usageIndex === data.usages.length - 1}
                $isGroupLast={usageIndex === data.usages.length - 1}
                $center>
                <Text fontFamily='NotoSansKR-Regular' fontSize={14} color={theme.text}>
                  {calcPreference(usage.count, data.reservedSum) + '%'}
                </Text>
              </TableDataCell>
            ))
          }
        </PreferenceColumnWrapper>
      </AgeGroupRow>
    );
  }

  return (
    <TopLevelWrapper $isDark={isDark}>
      {
        isEngagementLoading ?
          <AnimationBox>
            <LottieWrapper>
              <Lottie animationData={LoadingAnimation} loop={true} />
            </LottieWrapper>
          </AnimationBox>
          :
          isOpenPartnerGymSales ?
            <ContentWrapper>
              <TitleRow>
                <Text fontFamily='NotoSansKR-Bold' fontSize={20} color={theme.text}>
                  {calcDateText()}
                </Text>
                <Text fontFamily='NotoSansKR-Bold' fontSize={20} color={theme.text}>
                  {t('statistics.engagement.ageAnalysis')}
                </Text>
              </TitleRow>
              <SubtitleRow>
                <AgeRangeImage src={AgeGroupIcon} />
                <Text fontFamily='NotoSansKR-Medium' fontSize={20} color={theme.text}>
                  {t('statistics.engagement.mostAge') + ' : ' + engagementMaxText}
                </Text>
                <PercentageWrapper>
                  <Text fontFamily='NotoSansKR-Medium' fontSize={20} color={theme.brand500}>
                    {engagementTotalCount > 0 ? (engagementMaxCount / engagementTotalCount * 100).toFixed(1) : '0'}
                  </Text>
                  <PercentageSuffix>
                    <Text fontFamily='NotoSansKR-Medium' fontSize={12} color={theme.brand500}>%</Text>
                  </PercentageSuffix>
                </PercentageWrapper>
              </SubtitleRow>
              <ProgressBarGridWrapper>
                <ProgressBarRow>
                  {allAgeGroups.slice(0, 3).map((data, index) => renderProgressBarCard(data, index))}
                </ProgressBarRow>
                <ProgressBarRow>
                  {allAgeGroups.slice(3, 6).map((data, index) => renderProgressBarCard(data, index + 3))}
                </ProgressBarRow>
                <ProgressBarRow>
                  {allAgeGroups.slice(6, 9).map((data, index) => renderProgressBarCard(data, index + 6))}
                </ProgressBarRow>
              </ProgressBarGridWrapper>
              <TableSectionWrapper>
                <TableTitleWrapper>
                  <Text fontFamily='NotoSansKR-Medium' fontSize={14} color={theme.text}>
                    {t('statistics.engagement.ageUsageStats')}
                  </Text>
                </TableTitleWrapper>
                <TableContentWrapper>
                  <TableHeaderRow>
                    <TableHeaderCell width={287}>
                      <Text fontFamily='NotoSansKR-Regular' fontSize={14} color={theme.text}>
                        {t('statistics.engagement.colAge')}
                      </Text>
                    </TableHeaderCell>
                    <TableHeaderCell width={350}>
                      <Text fontFamily='NotoSansKR-Regular' fontSize={14} color={theme.text}>
                        {t('statistics.engagement.colMembership')}
                      </Text>
                    </TableHeaderCell>
                    <TableHeaderFlexCell>
                      <Text fontFamily='NotoSansKR-Regular' fontSize={14} color={theme.text}>
                        {t('statistics.engagement.colUsageRecord')}
                      </Text>
                    </TableHeaderFlexCell>
                    <TableHeaderFlexCell>
                      <Text fontFamily='NotoSansKR-Regular' fontSize={14} color={theme.text}>
                        {t('statistics.engagement.colPreference')}
                      </Text>
                    </TableHeaderFlexCell>
                  </TableHeaderRow>
                  <TableDividerLine />
                  <TableBodyWrapper>
                    {engagementStates.map((data, index) => renderAgeGroupRow(data, index))}
                  </TableBodyWrapper>
                </TableContentWrapper>
              </TableSectionWrapper>
            </ContentWrapper>
            :
            <NotOpenWrapper>
              <Text fontFamily='NotoSansKR-Bold' fontSize={15} color={theme.subText}>{t('statistics.partnerGymPrivate')}</Text>
              <Text fontFamily='NotoSansKR-Regular' fontSize={15} color={theme.subText}>{t('statistics.partnerGymPrivateDesc')}</Text>
            </NotOpenWrapper>
      }
    </TopLevelWrapper>
  );
}

const TopLevelWrapper = styled.div`
  display: flex;
  flex-direction: column;

  align-items: flex-start;
  justify-content: center;

  margin: 39.5px 0;
  padding: 20px 40px;

  width: 100%;
  max-width: 960px;

  gap: 10px;

  border-radius: 20px;
  background-color: ${props => props.theme.subBackground};
`;

const ContentWrapper = styled.div`
  display: flex;
  flex-direction: column;

  align-items: flex-start;

  width: 100%;

  gap: 10px;
`;

const TitleRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  overflow: hidden;

  width: 100%;
`;

const SubtitleRow = styled.div`
  display: flex;
  flex-direction: row;

  align-items: center;

  gap: 10px;
`;

const AgeRangeImage = styled.img`
  width: 32px;
  height: 30px;

  margin-top: 5px;
`;

const PercentageWrapper = styled.div`
  display: flex;
  flex-direction: row;

  align-items: center;

  gap: 1px;
`;

const PercentageSuffix = styled.div`
  display: flex;
  align-items: flex-end;

  padding-bottom: 2px;
`;

const CountWrapper = styled.div`
  display: flex;
  flex-direction: row;

  align-items: center;
  overflow: hidden;

  gap: 5px;
`;

const CountSuffix = styled.div`
  display: flex;
  align-items: flex-end;

  padding-bottom: 2px;
`;

const ProgressBarGridWrapper = styled.div`
  display: flex;
  flex-direction: column;

  align-items: flex-start;
  justify-content: center;

  padding: 20px 0;

  width: 100%;

  gap: 10px;
`;

const ProgressBarRow = styled.div`
  display: flex;
  flex-direction: row;

  align-items: center;

  width: 100%;

  gap: 40px;
`;

const ProgressBarCardWrapper = styled.div`
  display: flex;
  flex-direction: column;

  align-items: flex-start;
  justify-content: center;

  flex: 1;
  min-width: 0;
`;

const ProgressBarInfoRow = styled.div`
  display: flex;
  flex-direction: row;

  align-items: center;

  width: 100%;

  gap: 10px;
`;

const ProgressBarLabelWrapper = styled.div`
  display: flex;
  flex-direction: row;

  align-items: center;
  overflow: hidden;

  flex: 1;
  min-width: 0;

  gap: 10px;
`;

const ProgressBarTrack = styled.div`
  position: relative;

  display: flex;
  align-items: center;
  justify-content: center;

  width: 100%;
  height: 10px;

  border-radius: 1000px;
  background-color: ${props => props.theme.buttonFill3};
`;

const ProgressBarFill = styled.div`
  position: absolute;
  left: 0;
  top: 0;

  width: ${props => props.width}%;
  height: 10px;

  border-radius: 1000px;
  background-color: ${props => props.$barColor};
`;

const TableSectionWrapper = styled.div`
  display: flex;
  flex-direction: column;

  align-items: center;
  overflow: hidden;

  width: 100%;

  gap: 10px;
`;

const TableTitleWrapper = styled.div`
  display: flex;
  flex-direction: column;

  align-items: flex-start;
  justify-content: center;
  overflow: hidden;

  width: 100%;
`;

const TableContentWrapper = styled.div`
  display: flex;
  flex-direction: column;

  align-items: flex-start;
  overflow: hidden;

  width: 100%;

  border-radius: 5px;

  /* 모든 셀의 width에 padding/border 포함 — 헤더·바디 컬럼 폭 일치 //
     (전역 box-sizing reset이 없어 content-box 기본값이라 컬럼이 어긋남) */
  * {
    box-sizing: border-box;
  }
`;

const TableHeaderRow = styled.div`
  display: flex;
  flex-direction: row;

  align-items: center;
  overflow: hidden;

  width: 100%;
  height: 30px;

  border-radius: 5px 5px 0 0;
`;

const TableHeaderCell = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-start;
  overflow: hidden;

  padding: 0 10px;

  width: ${props => props.width}px;
`;

const TableHeaderFlexCell = styled.div`
  display: flex;
  flex: 1;

  align-items: center;
  justify-content: center;
  overflow: hidden;

  min-width: 0;
`;

const TableDividerLine = styled.div`
  width: 100%;
  height: 1px;

  margin: 7px 0;

  opacity: 0.5;
  background-color: ${props => props.theme.line};
`;

const TableBodyWrapper = styled.div`
  display: flex;
  flex-direction: column;

  align-items: flex-start;

  width: 100%;
  max-height: 450px;

  overflow-y: scroll;
  overflow-x: hidden;

  &::-webkit-scrollbar {
    width: 2px;
  }

  &::-webkit-scrollbar-thumb {
    background-color: ${props => props.theme.brand500};
    border-radius: 200px;
  }

  &::-webkit-scrollbar-track {
    background-color: transparent;
    border-radius: 200px;
  }
  
  border-radius: 20px;
`;

const AgeGroupRow = styled.div`
  display: flex;
  flex-direction: row;

  align-items: stretch;

  width: 100%;
`;

const AgeGroupLabelCell = styled.div`
  display: flex;
  flex-direction: row;

  align-items: stretch;
  overflow: hidden;

  width: 287px;

  background-color: ${props => props.theme.buttonFill};
  border-bottom: ${props => props.$isLast ? 'none' : `1px solid ${props.theme.brand500Alpha50}`};
`;

const AgePercentCell = styled.div`
  display: flex;

  align-items: center;
  justify-content: center;
  overflow: hidden;

  padding: 10px;

  width: 100px;

  flex-shrink: 0;
`;

const AgeNameCell = styled.div`
  display: flex;

  align-items: center;
  justify-content: center;
  overflow: hidden;

  padding: 10px;

  width: 107px;

  flex-shrink: 0;

  border-left: 1px solid ${props => props.theme.brand500};
`;

const AgeCountCell = styled.div`
  display: flex;
  flex-direction: row;

  align-items: center;
  justify-content: center;
  overflow: hidden;

  padding: 10px;

  width: 80px;

  flex-shrink: 0;

  border-left: 1px solid ${props => props.theme.brand500};
`;

const MembershipColumnWrapper = styled.div`
  display: flex;
  flex-direction: column;

  align-items: flex-start;
  justify-content: center;

  width: 350px;

  border-right: 1px solid ${props => props.theme.line};
`;

const UsageColumnWrapper = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;

  align-items: flex-start;
  justify-content: center;

  min-width: 0;

  border-right: 1px solid ${props => props.theme.line};
`;

const PreferenceColumnWrapper = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;

  align-items: flex-start;
  justify-content: center;

  min-width: 0;
`;

const TableDataCell = styled.div`
  display: flex;
  flex: 1;

  align-items: center;
  justify-content: ${props => props.$center ? 'center' : 'flex-start'};
  overflow: hidden;

  padding: 10px;

  width: 100%;
  min-height: 0;

  border-bottom: ${props => {
    if (props.$isLast) return 'none';
    if (props.$isGroupLast) return `1px solid ${props.theme.brand500Alpha50}`;
    return `1px solid ${props.theme.line}`;
  }};
`;

const AnimationBox = styled.div`
  display: flex;
  flex-direction: column;

  justify-content: center;
  align-items: center;

  width: 100%;
  height: 300px;
`;

const LottieWrapper = styled.div`
  display: flex;
  flex-direction: column;

  justify-content: center;
  align-items: center;

  width: 160px;
  height: 160px;
`;

const NotOpenWrapper = styled.div`
  display: flex;
  flex-direction: column;

  justify-content: center;
  align-items: center;

  width: 100%;
  padding: 40px 0;
`;

const Text = styled.p`
  font-family: ${props => props.fontFamily};
  font-size: ${props => props.fontSize}px;

  color: ${props => props.color};

  margin: 0;
  cursor: default;
`;